/**
 * Internal Linear crew-token contracts (SCH-5844).
 *
 * The backend is the ONLY minter of each crew bot's Linear `client_credentials`
 * token: when an app mints a new token, Linear revokes that app's previous one,
 * so a second minter (a hand-written key file, a hand-set proxy bearer) and the
 * backend kept revoking each other's tokens.
 *
 * - `POST /internal/integrations/linear/refresh` (INTERNAL_API_KEY): force a new
 *   mint for one identity and push it to the agent's consumer. Answers the new
 *   expiry, NEVER the token.
 * - `POST /internal/integrations/linear/token` (LINEAR_TOKEN_SYNC_KEY only): the
 *   factory-host sync script's read of the current token, which it hands to the
 *   agent's exe.dev `linear-<agent>` http-proxy bearer. Crew labels only.
 *
 * Only the wire shape lives here. Each endpoint's error enum stays beside its
 * handler in the backend.
 */

import { z } from 'zod'

/** The crew bots whose Linear token the backend mints and pushes to factory-host. */
export const LINEAR_CREW_LABELS = ['scotty', 'sparky', 'raven', 'hunter', 'jerne'] as const

export const LinearCrewLabelSchema = z.enum(LINEAR_CREW_LABELS)

export type LinearCrewLabel = z.infer<typeof LinearCrewLabelSchema>

export function isLinearCrewLabel(label: string): label is LinearCrewLabel {
  return (LINEAR_CREW_LABELS as readonly string[]).includes(label)
}

/** POST /internal/integrations/linear/refresh */
export const InternalLinearTokenRefreshRequestSchema = z.object({
  /** The Linear identity label, e.g. "scotty". Any registered PLATFORM label. */
  label: z.string().min(1)
})

export type InternalLinearTokenRefreshRequest = z.infer<typeof InternalLinearTokenRefreshRequestSchema>

export const InternalLinearTokenRefreshResponseSchema = z.object({
  ok: z.literal(true),
  /** ISO-8601 expiry of the token now stored. Null when the row records none. */
  tokenExpiresAt: z.string().nullable(),
  /** Whether this call delivered the new token to the agent's consumer. */
  pushed: z.boolean()
})

export type InternalLinearTokenRefreshResponse = z.infer<typeof InternalLinearTokenRefreshResponseSchema>

/** POST /internal/integrations/linear/token */
export const InternalLinearTokenReadRequestSchema = z.object({
  label: LinearCrewLabelSchema
})

export type InternalLinearTokenReadRequest = z.infer<typeof InternalLinearTokenReadRequestSchema>

export const InternalLinearTokenReadResponseSchema = z.object({
  ok: z.literal(true),
  accessToken: z.string().min(1),
  tokenExpiresAt: z.string().nullable()
})

export type InternalLinearTokenReadResponse = z.infer<typeof InternalLinearTokenReadResponseSchema>
